import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { SimpleInput } from 'app/shared/input';
import { Button } from 'app/shared/button';
import { addDataToStore } from 'store/actions/user-data';
import { useUserService } from 'hooks/iam/user';
import styles from './payout.module.css';

export const PayoutAccount = () => {
  const dispatch = useDispatch();
  const user_data = useSelector((store) => store.user_data);
  const agency = useSelector((store) => store.metadata.agency);
  const { payout_account } = user_data;
  const { updateUserData } = useUserService();

  // The account must match the BVN, whose names are stored (uppercased) on the
  // agency. Pre-fill it and keep it read-only so it can't drift from the BVN.
  const bvn_name = `${agency?.firstname || ''} ${agency?.lastname || ''}`.trim().toUpperCase();
  const has_account = !!payout_account?.number;

  const [mode, setMode] = useState('view');
  const [form_data, setFormData] = useState({ bank: '', name: bvn_name, number: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFormData({
      bank: payout_account?.bank || '',
      name: payout_account?.name || bvn_name,
      number: payout_account?.number || ''
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payout_account, bvn_name]);

  const handleChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const submit = async () => {
    if (!form_data.bank) return toast.error('Please enter the bank name.');
    if (!form_data.number || form_data.number.length !== 10) {
      return toast.error('Account number must be 10 digits long.');
    }

    const data = { payout_account: { ...form_data, name: bvn_name } };
    setSaving(true);
    const result = await updateUserData(user_data.id, { data });
    setSaving(false);

    if (!result) return;
    toast.success('Payout account updated.');
    dispatch(addDataToStore({ ...user_data, ...data }));
    setMode('view');
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.title}>Payout account</span>
        {mode === 'view' && has_account && (
          <button className={styles.headerAction} onClick={() => setMode('edit')}>
            Update
          </button>
        )}
      </div>

      <div className={styles.body}>
        {mode === 'view' ? (
          has_account ? (
            <>
              <div className={styles.field}>
                <span className={styles.label}>Bank name</span>
                <span className={styles.value}>{payout_account.bank || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Account name</span>
                <span className={styles.value}>{payout_account.name || bvn_name || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Account number</span>
                <span className={styles.value}>{payout_account.number || 'N/A'}</span>
              </div>
            </>
          ) : (
            <div className={styles.empty}>
              <p className={styles.emptyBody}>
                Add a payout account so we know where to send your earnings.
              </p>
              <Button text="Add payout account" onClick={() => setMode('edit')} />
            </div>
          )
        ) : (
          <>
            <div className={styles.field}>
              <span className={styles.label}>Bank name</span>
              <SimpleInput value={form_data.bank} onInput={(v) => handleChange('bank', v)} />
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Account name</span>
              <SimpleInput value={form_data.name} disabled />
              <span className={styles.hint}>Taken from your verified BVN.</span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Account number</span>
              <SimpleInput
                type="number"
                value={form_data.number}
                onInput={(v) => handleChange('number', v)}
              />
            </div>
            <div className={styles.actions}>
              <Button text="Save" loading={saving} onClick={submit} />
              <Button
                type="secondary"
                text="Cancel"
                onClick={() => {
                  setMode('view');
                  setFormData({
                    bank: payout_account?.bank || '',
                    name: payout_account?.name || bvn_name,
                    number: payout_account?.number || ''
                  });
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
